import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { txApi } from '../txApi.js';
import { createUtmSession, utmSpreadsheetConfig, utmSpreadsheetOutput, utmSpreadsheetError, utmSpreadsheetComplete } from '../utmGrid.js';
import SpreadsheetGrid from './_SpreadsheetGrid.jsx';
import './utm.css';

export function UtmHistory({ groupId, platformId, readOnly, onRestored }) {
  const [versions, setVersions] = useState([]), [snapshot, setSnapshot] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState(''), [busy, setBusy] = useState(false);
  useEffect(() => { let alive = true; setLoadingHistory(true); txApi.getUtmHistory(groupId, platformId).then(d => { if (alive) setVersions(d.versions); }).catch(e => { if (alive) setError(e.message); }).finally(() => { if (alive) setLoadingHistory(false); }); return () => { alive = false; }; }, [groupId, platformId]);
  const restore = async () => {
    if (!window.confirm('Restore these UTM rows? Current UTM rows will be saved in history first.')) return;
    setBusy(true); setError('');
    try {
      const current = await txApi.getUtmRows(groupId, snapshot.platform_id);
      await txApi.restoreUtmVersion(groupId, snapshot.id, { expected_revision: current.revision, expected_version_id: current.setup.id, expected_setup_revision: current.setup.revision });
      setSnapshot(null); onRestored?.();
      setVersions((await txApi.getUtmHistory(groupId, platformId)).versions);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return <div className="tx-utm">
    <h3>UTM history</h3>{error && <div className="o-state o-error" role="alert">{error}</div>}
    {loadingHistory && <p className="o-state">Loading UTM history…</p>}
    {!loadingHistory && !versions.length && <p className="o-note">No earlier UTM snapshots saved.</p>}
    <div className="utm-toolbar">{versions.map(v => <button className="o-btn o-btn-sm" key={v.id} onClick={async () => { try { setSnapshot((await txApi.getUtmVersion(groupId, v.id)).version); } catch (e) { setError(e.message); } }}>{v.platform_name} · {new Date(v.created_at).toLocaleString()} · {v.row_count} rows</button>)}</div>
    {snapshot && <div className="o-panel utm-field"><div className="utm-toolbar"><strong>Saved UTM snapshot</strong><button className="o-btn" onClick={() => setSnapshot(null)}>Close preview</button>{!readOnly && <button className="o-btn o-btn-primary" disabled={busy} onClick={restore}>Restore snapshot</button>}</div>
      {snapshot.snapshot.rows.map((r, i) => <div key={r.id} className="utm-review-detail"><strong>Row {i + 1}</strong>{Object.entries(r.values).map(([id, value]) => <p key={id}>{r.baseline?.[id]?.name || id}: {value}</p>)}<p className="utm-url">{r.url || 'No generated URL'}</p><p className="o-note">{r.status}</p></div>)}
    </div>}
  </div>;
}

const EMPTY_FIELDS = [];
const EMPTY_DICTIONARY = {};
const createRowId = () => crypto.randomUUID();

export default function UtmGrid({ groupId, platformId, clientId, readOnly, sessions }) {
  const key = `${clientId}:${groupId}:${platformId}`;
  const session = useMemo(() => {
    if (!sessions.current[key]) sessions.current[key] = createUtmSession(s => txApi.saveUtmRows(groupId, platformId, {
      rows: s.rows, expected_revision: s.revision, expected_version_id: s.setup.id, expected_setup_revision: s.setup.revision,
    }), { rows: [], setup: null, options: {}, diagnostics: {}, revision: 0 });
    return sessions.current[key];
  }, [key]);
  const state = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const [loading, setLoading] = useState(!state.setup), [error, setError] = useState('');
  const historyRef = useMemo(() => (session.historyRef ||= { current: {} }), [session]);
  const fields = state.setup?.fields || EMPTY_FIELDS;
  const rows = state.rows;
  const taxonomy = useMemo(() => utmSpreadsheetConfig(fields, state.options), [fields, state.options]);
  const load = async (discard = false) => {
    if (session.getSnapshot().dirty && !discard) { setError('Save your pending edits before refreshing sources.'); return; }
    setLoading(true); setError('');
    try {
      await txApi.waitForTaxonomySaves(groupId, platformId);
      await session.refresh(() => txApi.getUtmRows(groupId, platformId), { discard });
    }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => txApi.waitForTaxonomySaves(groupId, platformId)).then(async () => {
      if (!alive) return;
      await session.refresh(() => txApi.getUtmRows(groupId, platformId), { isCurrent: () => alive });
    }).catch(e => { if (alive) setError(e.message); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; session.flush().catch(() => {}); };
  }, [session]);
  useEffect(() => {
    const leave = e => { if (session.getSnapshot().dirty) { session.flush().catch(() => {}); e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', leave);
    return () => window.removeEventListener('beforeunload', leave);
  }, [session]);
  const setRows = useCallback(updater => {
    if (readOnly) return;
    const current = session.getSnapshot().rows;
    session.change(typeof updater === 'function' ? updater(current) : updater);
  }, [session, readOnly]);
  const change = updater => {
    if (readOnly) return;
    const stack = (historyRef.current[key] ||= { undo: [], redo: [] });
    stack.undo.push(session.getSnapshot().rows);
    if (stack.undo.length > 100) stack.undo.shift();
    stack.redo.length = 0;
    setRows(updater);
  };
  const adapter = useMemo(() => ({
    outputLabel: 'Generated URL', output: utmSpreadsheetOutput, fieldError: (row, field) => utmSpreadsheetError(row, field, state.diagnostics),
    rowError: row => row.errors?._formula || row.errors?._setup || row.review?._formula?.reason || (Object.keys(row.review || {}).some(id => id !== '_formula' && !fields.some(field => field.id === id)) ? 'Removed fields need review.' : null),
    isComplete: utmSpreadsheetComplete, createRowId, save: session.flush, status: state.status,
    batchPreview: values => fields.filter(field => values[field.id]).map(field => `${field.name}: ${values[field.id]}`).join(' · '),
    outputHint: row => state.dirty ? 'Pending validation · last saved URL' : row.status === 'valid' ? ''
      : row.url ? 'Last saved URL · outdated' : row.errors?._formula || 'Complete required fields',
  }), [session, state.status, state.dirty, fields, state.diagnostics]);
  return <div className="tx-utm utm-sheet">
    {(error || state.error) && <div className="o-state o-error" role="alert">{error || state.error} <button className="o-btn" onClick={() => { if (!state.dirty || window.confirm('Discard unsaved UTM edits and reload?')) load(true); }}>Reload</button></div>}
    {loading && <div className="o-state">Loading UTM rows and saved taxonomy names…</div>}
    {!loading && !state.setup && <div className="o-state">No UTM fields configured. Add fields in UTM Setup first.</div>}
    {!loading && state.setup && <>
      {!fields.some(f => f.role === 'destination') && <div className="o-hint">Add a destination field in UTM Setup before generating URLs. Existing saved URLs are preserved.</div>}
      {!state.setup.formula && <div className="o-hint">Formula required. Save a URL generation formula in UTM Setup. Existing saved URLs are preserved.</div>}
      <SpreadsheetGrid taxonomy={taxonomy} dictValues={EMPTY_DICTIONARY} rows={rows} setRows={setRows}
        readOnly={readOnly} adapter={adapter} historyRef={historyRef} historyKey={key} allowBatchGenerate={false}
        errorDetails={rows.some(row => Object.keys(row.review || {}).length) && <section aria-label="Review saved UTM values">
          {rows.map((row, index) => Object.entries(row.review || {}).map(([id, info]) => <div className="utm-review-detail" key={`${row.id}:${id}`}>
            <strong>Row {index + 1} · {id === '_formula' ? 'Generated URL' : info.after?.name || info.before?.name || 'Removed field'}</strong>
            <p>{info.reason} {id === '_formula' ? `Last saved URL: ${row.url || 'None'}` : `Saved value: ${row.values[id] || 'Empty'}`}</p>
            <details><summary>Previous and current configuration</summary><pre>{JSON.stringify({ previous: id === '_formula' ? info.before : info.before?.definition || null, current: info.after || null }, null, 2)}</pre></details>
            {!readOnly && <button className="o-btn o-btn-sm" onClick={() => change(current => current.map(r => r.id === row.id ? { ...r, accept: [...new Set([...(r.accept || []), id])] } : r))}>{id === '_formula' ? 'Accept formula and regenerate' : 'Keep valid value / accept removal'}</button>}
          </div>))}
        </section>} />
    </>}
  </div>;
}
